import { useEffect, useState } from 'react';

import './duration-input.scss';

export interface DurationInputProps {
	defaultValue?: number | null;
	className?: string;
	disabled?: boolean;
	style?: Record<string, string>;
	onChange?: (value: number) => void;
	onBlur?: (value: number) => void;
}

const MIN = 0;
const MAX = 99;

const msToHours = (value: number) => Math.floor(value / (1000 * 60 * 60));
const hoursToMs = (hours: number) => hours * (1000 * 60 * 60);

export const DurationInput: React.FC<DurationInputProps> = ({
	defaultValue,
	onChange,
	onBlur,
	className,
	disabled,
	style,
}) => {
	const [duration, setDuration] = useState<string>('');

	useEffect(() => {
		if (defaultValue !== undefined && defaultValue !== null) {
			setDuration(String(msToHours(defaultValue)));
		} else {
			setDuration('0');
		}
	}, [defaultValue]);

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const rawValue = e.target.value;

		// Разрешаем только целые числа и пустую строку
		if (!/^\d*$/.test(rawValue)) {
			return;
		}

		// Если строка пустая, устанавливаем "0"
		if (rawValue === '') {
			setDuration('0');
			onChange?.(0);
			return;
		}

		// Преобразуем в число и проверяем границы
		const numValue = parseInt(rawValue, 10);
		let finalValue = numValue;

		if (numValue < MIN) finalValue = MIN;
		if (numValue > MAX) finalValue = MAX;

		setDuration(String(finalValue));
		onChange?.(hoursToMs(finalValue));
	};

	const handleBlur = () => {
		// При потере фокуса убираем лидирующие нули
		let numValue = parseInt(duration, 10);

		if (isNaN(numValue)) {
			numValue = 0;
		}

		// Проверяем границы
		if (numValue < MIN) numValue = MIN;
		if (numValue > MAX) numValue = MAX;

		const displayValue = String(numValue);
		const msValue = hoursToMs(numValue);

		// Обновляем отображаемое значение
		if (duration !== displayValue) {
			setDuration(displayValue);
		}

		// Вызываем обработчики
		onChange?.(msValue);
		onBlur?.(msValue);
	};

	return (
		<div className={`duration ${className ?? ''}`} style={style}>
			<input
				onChange={handleChange}
				onBlur={handleBlur}
				disabled={disabled}
				type="text"
				className="input"
				value={duration}
				placeholder="0"
				inputMode="numeric" // Показывает цифровую клавиатуру на мобильных
				pattern="\d*" // Подсказка для браузера, что ожидаются только цифры
			/>
		</div>
	);
};
